import { useCallback,useState } from 'react';
import { useParams } from 'react-router-dom';

import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmFetchEntities } from 'common/hooks/usePalmFetchEntities';
import { usePalmFilters } from 'common/hooks/usePalmFilters';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { fetchRoles } from 'common/services/api/Roles/Roles.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, Operations } from 'common/types/TypeDefs';

import { RoleDeleteDialog } from './RoleDeleteDialog';
import { RolesCreateContent } from './RolesEditDrawerContent';
import { addNewFilter, clearFilters, removeFilter, selectFilters } from './rolesSlice';

export const RolesTab = () => {
    const typeName = 'Role';

    const [sortModel, setSortModel] = usePalmSortModel();
    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager ]);
    const shouldDisableOpenButton = usePalmButtonEvaluator([ Roles.Admin, Roles.AppManager, Roles.MultiTenantRO, Roles.UserManager ]);

    const { suiteId = '', tenantId = '' } = useParams();
    const [processing, setProcessing] = useState<boolean>(false);

    const filterProp = usePalmFilters(
        selectFilters,
        addNewFilter,
        removeFilter,
        clearFilters
    );

    const onGetRowLink = useCallback((roleId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.Roles}/${roleId}/${Routes.Users}`;
    }, []);

    const fetchEntities = usePalmFetchEntities(
        typeName,
        { tenantId },
        fetchRoles,
        setProcessing);

    const actionDefinitions = [
        { action: Operations.Open, disabled: shouldDisableOpenButton, urlToOpen: onGetRowLink },
        { action: Operations.Edit, disabled: shouldDisableButtons },
        { action: Operations.Delete, disabled: shouldDisableButtons }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'id', columnName: 'Role ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'name', columnName: 'Role Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        title={typeName}
        contentFunction={RolesCreateContent}
        isCreateButtonDisabled={shouldDisableButtons}
        getRowLink={shouldDisableOpenButton ? undefined : onGetRowLink}
        deleteFunction={RoleDeleteDialog}
        filter={filterProp}
        processing={processing}
        refreshDataGridClick={() => fetchEntities()}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;

};
